import React, { useRef } from 'react'
import styled, { keyframes } from 'styled-components'
import { motion, Variants, useInView } from 'framer-motion'
import {
  Database,
  FileText,
  BarChart2,
  UserCheck,
  Target,
  TrendingUp,
  BookOpen,
} from 'lucide-react'
import {
  AnimatedSection,
  StaggerContainer,
  StaggerItem,
  fadeInLeft,
  scaleIn,
  staggerContainer,
  smoothEase,
} from './animations/AnimatedSection'

const nodeColors = [
  '#4A90D9', // Research     — blue
  '#9B59B6', // AI Analysis  — purple
  '#E67E22', // Experts      — orange
  '#27AE60', // People Action — green
  '#E74C3C', // Impact Data  — red
  '#F39C12', // Learning     — amber
]

const satelliteVariants: Variants = {
  hidden: { opacity: 0, scale: 0 },
  visible: (i: number) => ({
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.5,
      delay: 0.2 + i * 0.12,
      ease: [0.34, 1.56, 0.64, 1] as [number, number, number, number],
    },
  }),
}

const centralNodeVariants: Variants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.6, delay: 0.2, ease: smoothEase },
  },
}

const GraphicFeatureSection = () => {
  const wheelRef = useRef<HTMLDivElement>(null)
  const isInView = useInView(wheelRef, { once: true, amount: 0.5 })

  const satellites = [
    { icon: <FileText size={24} />, title: 'Research' },
    {
      icon: <BarChart2 size={24} />,
      title: (
        <>
          AI
          <br />
          Analysis
        </>
      ),
    },
    { icon: <UserCheck size={24} />, title: 'Experts' },
    {
      icon: <Target size={24} />,
      title: (
        <>
          People
          <br />
          Action
        </>
      ),
    },
    {
      icon: <TrendingUp size={24} />,
      title: (
        <>
          Impact
          <br />
          Data
        </>
      ),
    },
    { icon: <BookOpen size={24} />, title: 'Learning' },
  ]

  const radius = 220
  const center = 300
  // Center circle radius — spokes will stop here
  const hubRadius = 78

  const getCoordinates = (index: number) => {
    const angle = (index * 60 - 90) * (Math.PI / 180)
    return {
      x: center + radius * Math.cos(angle),
      y: center + radius * Math.sin(angle),
    }
  }

  // Shorten spoke so it starts at the hub edge, not the center
  const getSpokeStart = (index: number) => {
    const angle = (index * 60 - 90) * (Math.PI / 180)
    return {
      x: center + hubRadius * Math.cos(angle),
      y: center + hubRadius * Math.sin(angle),
    }
  }

  return (
    <Container>
      <InnerContainer>
        <StaggerContainer
          variants={staggerContainer}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '56px',
            maxWidth: '379px',
            zIndex: 10,
          }}
        >
          <StaggerItem variants={fadeInLeft}>
            <FeatureBlock color={nodeColors[0]}>
              <FeatureTitle>Evidence-Based</FeatureTitle>
              <FeatureDescription>
                Grounded in peer-reviewed science and aligned with UN SDG
                frameworks
              </FeatureDescription>
            </FeatureBlock>
          </StaggerItem>

          <StaggerItem variants={fadeInLeft}>
            <FeatureBlock color={nodeColors[1]}>
              <FeatureTitle>Globally Scalable</FeatureTitle>
              <FeatureDescription>
                Designed for institutions, governments, and communities
                worldwide
              </FeatureDescription>
            </FeatureBlock>
          </StaggerItem>

          <StaggerItem variants={fadeInLeft}>
            <FeatureBlock color={nodeColors[2]}>
              <FeatureTitle>Story-Driven</FeatureTitle>
              <FeatureDescription>
                Translating complex research into compelling narratives that
                inspire action
              </FeatureDescription>
            </FeatureBlock>
          </StaggerItem>

          <StaggerItem variants={fadeInLeft}>
            <FeatureBlock color={nodeColors[3]}>
              <FeatureTitle>Action-Oriented</FeatureTitle>
              <FeatureDescription>
                Every interaction converts knowledge into measurable,
                coordinated impact
              </FeatureDescription>
            </FeatureBlock>
          </StaggerItem>
        </StaggerContainer>

        <AnimatedSection variants={scaleIn} threshold={0.2}>
          <GraphicContainer>
            <WheelContainer ref={wheelRef}>
              <GlowBackground />

              <svg
                viewBox="0 0 600 600"
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  overflow: 'visible',
                  zIndex: 5,
                }}
              >
                <defs>
                  <radialGradient id="centerGradient" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#E5D1FA" />
                    <stop offset="100%" stopColor="#BBE148" />
                  </radialGradient>
                  {/* Clip so spokes never bleed into center circle */}
                  <clipPath id="spokeClip">
                    <path
                      d={`
                        M 0 0 H 600 V 600 H 0 Z
                        M ${center} ${center}
                        m -${hubRadius} 0
                        a ${hubRadius} ${hubRadius} 0 1 0 ${hubRadius * 2} 0
                        a ${hubRadius} ${hubRadius} 0 1 0 -${hubRadius * 2} 0
                      `}
                      fillRule="evenodd"
                    />
                  </clipPath>
                </defs>

                {/* Outer Dashed Ring */}
                <motion.circle
                  cx={center}
                  cy={center}
                  r={radius}
                  fill="none"
                  stroke="#6ab04c"
                  strokeWidth="2.5"
                  strokeDasharray="10 10"
                  initial={{ strokeDashoffset: 0, opacity: 0 }}
                  whileInView={{ strokeDashoffset: [0, -20], opacity: 0.75 }}
                  viewport={{ once: true }}
                  transition={{
                    strokeDashoffset: {
                      duration: 2,
                      delay: 0.3,
                      ease: 'linear',
                      repeat: Infinity,
                      repeatType: 'loop',
                    },
                    opacity: { duration: 0.8, delay: 0.3 },
                  }}
                />

                {/* Spokes — rendered BEFORE hub so hub sits on top */}
                {satellites.map((_, i) => {
                  const start = getSpokeStart(i)
                  const coords = getCoordinates(i)
                  const spokeDelay = 0.45 + i * 0.12
                  const color = nodeColors[i]

                  return (
                    <motion.line
                      key={`spoke-${i}`}
                      x1={start.x}
                      y1={start.y}
                      x2={coords.x}
                      y2={coords.y}
                      stroke={color}
                      strokeWidth="1.5"
                      strokeDasharray="5 5"
                      clipPath="url(#spokeClip)"
                      initial={{ strokeDashoffset: 40, opacity: 0 }}
                      whileInView={{
                        strokeDashoffset: [40, 0, -40],
                        opacity: 0.5,
                      }}
                      viewport={{ once: true }}
                      transition={{
                        strokeDashoffset: {
                          duration: 1.5,
                          delay: spokeDelay,
                          ease: 'linear',
                          repeat: Infinity,
                          repeatType: 'loop',
                        },
                        opacity: { duration: 0.4, delay: spokeDelay },
                      }}
                    />
                  )
                })}
              </svg>

              {/* Nodes Overlay — using HTML for better cross-browser compatibility */}
              <NodesOverlay>
                {/* Satellite Nodes */}
                {satellites.map((sat, i) => {
                  const coords = getCoordinates(i)
                  const color = nodeColors[i]

                  return (
                    <NodeWrapper
                      key={`node-${i}`}
                      style={{
                        left: `${(coords.x / 600) * 100}%`,
                        top: `${(coords.y / 600) * 100}%`,
                        width: '15%',
                        height: '15%',
                      }}
                    >
                      <motion.div
                        initial="hidden"
                        animate={isInView ? 'visible' : 'hidden'}
                        custom={i}
                        variants={satelliteVariants}
                        style={{ width: '100%', height: '100%' }}
                      >
                        <SatelliteNode
                          style={{
                            borderColor: color,
                          }}
                        >
                          {React.cloneElement(sat.icon as React.ReactElement, {
                            color,
                          })}
                          <SatelliteTitle>{sat.title}</SatelliteTitle>
                        </SatelliteNode>
                      </motion.div>
                    </NodeWrapper>
                  )
                })}

                {/* Hub Node — rendered LAST with higher z-index if needed */}
                <NodeWrapper
                  style={{
                    left: '50%',
                    top: '50%',
                    width: '25%',
                    height: '25%',
                  }}
                >
                  <motion.div
                    initial="hidden"
                    animate={isInView ? 'visible' : 'hidden'}
                    variants={centralNodeVariants}
                    style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <CentralNode>
                      <Database size={28} color="#5a3e8a" />
                      <CentralTitle>Civic AI</CentralTitle>
                      <CentralSubtitle>Engine</CentralSubtitle>
                    </CentralNode>
                  </motion.div>
                </NodeWrapper>
              </NodesOverlay>
            </WheelContainer>
          </GraphicContainer>
        </AnimatedSection>
      </InnerContainer>
    </Container>
  )
}

// ─── Keyframes ───────────────────────────────────────────────────────────────

const glowPulse = keyframes`
  0%   { transform: translate(-50%, -50%) scale(0.95); opacity: 0.4; }
  50%  { transform: translate(-50%, -50%) scale(1.05); opacity: 0.6; }
  100% { transform: translate(-50%, -50%) scale(0.95); opacity: 0.4; }
`

// ─── Styled Components ───────────────────────────────────────────────────────

const Container = styled.section`
  width: 100%;
  background-color: #faf9f6;
  background-image: linear-gradient(
      180deg,
      #ffffff 0%,
      rgba(255, 255, 255, 0) 100%
    ),
    url('https://www.transparenttextures.com/patterns/cream-paper.webp');
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 80px 40px;
  position: relative;

  @media (max-width: 1150px) {
    padding: 64px 24px;
  }
`

const InnerContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 120px;
  width: 100%;
  max-width: 1280px;

  @media (max-width: 1150px) {
    flex-direction: column;
    gap: 64px;
  }
`

const FeatureBlock = styled.div<{ color: string }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-left: 24px;
  // border-left: 3px solid #eaeaea;
  transition: all 0.3s ease-in-out;
  cursor: default;
  border-left: 3px solid ${({ color }) => color};

  // &:hover {
  //   border-left-color: ${({ color }) => color};
  //   transform: translateX(4px);
  }

  @media (max-width: 1150px) {
    border-left: none;
    padding-left: 0;
    align-items: center;
    border-top: 3px solid #eaeaea;
    padding-top: 24px;
    width: 100%;

    &:hover {
      border-top-color: ${({ color }) => color};
      border-left-color: transparent;
      transform: translateY(4px);
    }

    &:first-child {
      border-top: none;
      padding-top: 0;
    }
  }
`

const FeatureTitle = styled.h3`
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 22px;
  line-height: 30px;
  letter-spacing: -0.02em;
  color: #2a2a2a;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 26px;
    line-height: 34px;
    text-align: center;
  }
`

const FeatureDescription = styled.p`
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 26px;
  color: #5e5e5e;
  margin: 0;
`

const GraphicContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
  position: relative;
  width: min(600px, 90vw);
  flex-shrink: 0;
`

const WheelContainer = styled.div`
  position: relative;
  width: 100%;
  /* Keep it square so the SVG viewBox scales correctly */
  aspect-ratio: 1 / 1;
`

const GlowBackground = styled.div`
  position: absolute;
  width: 95%;
  height: 95%;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  background: radial-gradient(
    50% 50% at 50% 50%,
    rgba(229, 209, 250, 0.45) 0%,
    rgba(187, 225, 72, 0.15) 60%,
    rgba(255, 255, 255, 0) 100%
  );
  border-radius: 50%;
  pointer-events: none;
  animation: ${glowPulse} 8s ease-in-out infinite;
`

const NodesOverlay = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 10;
`

const NodeWrapper = styled.div`
  position: absolute;
  transform: translate(-50%, -50%);
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: auto;
`

const CentralNode = styled.div`
  width: 100%;
  height: 100%;
  background: radial-gradient(circle at 50% 50%, #f0e4ff 0%, #bbe148 100%);

  border: 2px solid rgba(187, 225, 72, 0.6);
  box-shadow:
    0px 0px 0px 6px rgba(187, 225, 72, 0.15),
    0px 12px 32px -8px rgba(90, 62, 138, 0.3);
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 3px;
  isolation: isolate;
`

const CentralTitle = styled.h4`
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 18px;
  text-align: center;
  color: #3a2a5a;
  margin: 0;
`

const CentralSubtitle = styled.span`
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 13px;
  text-align: center;
  color: #5a4a7a;
  margin: 0;
`

const SatelliteNode = styled(motion.div)`
  width: 100%;
  height: 100%;

  background: #ffffff;
  border-width: 2px;
  border-style: solid;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 8px;
  gap: 6px;
  transition: all 0.3s ease;
  cursor: pointer;
  box-shadow: 0px 4px 14px -4px rgba(0, 0, 0, 0.08);

  &:hover {
    transform: scale(1.1);
    box-shadow: 0px 10px 24px -6px rgba(0, 0, 0, 0.15);
  }
`

const SatelliteTitle = styled.span`
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 11px;
  line-height: 14px;
  text-align: center;
  color: #2a2a2a;
  margin: 0;
`

export default GraphicFeatureSection
